'use client';

import React, { useEffect, useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import { useTranslation } from '@/hooks/useTranslation';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';

interface BlogPost {
    id: string;
    title: string;
    excerpt: string;
    content: string;
    author: string;
    category: string;
    featured_image: string | null;
    status: string;
    created_at: string;
}

export default function BlogPage() {
    const { t } = useTranslation();
    const router = useRouter();
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchPosts = async () => {
            const supabase = createClient();
            const { data, error } = await supabase
                .from('blog_posts')
                .select('*')
                .eq('status', 'published')
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Error fetching blog posts:', error);
            } else if (data) {
                setPosts(data);
            }
            setLoading(false);
        };

        fetchPosts();
    }, []);

    const handleReadMore = (postId: string) => {
        router.push(`/blog/${postId}`);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <>
            <Header />
            <main className="min-h-screen bg-background">
                {/* Hero */}
                <section className="py-20 px-6 bg-gradient-to-b from-card to-background border-b border-border">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-5xl font-headline font-bold text-foreground mb-6">{t('blogTitle')}</h1>
                        <p className="text-xl text-muted-foreground">
                            {t('blogSubtitle')}
                        </p>
                    </div>
                </section>

                {/* Posts */}
                <section className="py-20 px-6">
                    <div className="max-w-4xl mx-auto space-y-8">
                        {loading ? (
                            <div className="text-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                                <p className="mt-4 text-muted-foreground">Loading blog posts...</p>
                            </div>
                        ) : posts.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-muted-foreground">No blog posts available yet.</p>
                            </div>
                        ) : (
                            posts.map((post) => (
                                <article key={post.id} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                                    {post.featured_image && (
                                        <div className="h-64 overflow-hidden">
                                            <AppImage
                                                src={post.featured_image}
                                                alt={post.title}
                                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                            />
                                        </div>
                                    )}
                                    <div className="p-8">
                                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                                            <span className="px-3 py-1 bg-primary/10 text-primary rounded-full font-semibold">{post.category}</span>
                                            <span>{formatDate(post.created_at)}</span>
                                            <span>By {post.author}</span>
                                        </div>
                                        <h2
                                            className="text-2xl font-headline font-bold text-foreground mb-3 hover:text-primary transition-colors cursor-pointer"
                                            onClick={() => handleReadMore(post.id)}
                                        >
                                            {post.title}
                                        </h2>
                                        <p className="text-muted-foreground mb-4">{post.excerpt}</p>
                                        <button
                                            onClick={() => handleReadMore(post.id)}
                                            className="text-primary font-semibold hover:underline"
                                        >
                                            {t('readMore')} →
                                        </button>
                                    </div>
                                </article>
                            ))
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
